import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component, Inject, InjectionToken, NgModule } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FieldSchemaIdentifier, ObjectType, SchemaService } from '@vendasta/auxiliary-data-components';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { AuxiliaryDataTableService } from '../auxiliary-data-table.service';
import { SELECTED_OBJECT_TYPE$$ } from '../tokens';
import { BaseColumnComponent } from './base-column.component';
import { FieldSelectedForEditService } from './field-selected-for-edit.service';

interface DialogData {
  fieldName: string;
}

@Component({
  selector: 'aux-archive-alert-dialog',
  template: `
    <h2 mat-dialog-title>
      {{ 'ADMINISTRATION.CUSTOM_FIELDS.ARCHIVE_DIALOG.TITLE' | translate }}
    </h2>
    <div class="dialog-content" mat-dialog-content>
      <p>
        <span
          [innerHTML]="'ADMINISTRATION.CUSTOM_FIELDS.ARCHIVE_DIALOG.CONTENT' | translate: { fieldName: data.fieldName }"
        ></span>
        <a href="/automations" target="_blank">
          {{ 'ADMINISTRATION.CUSTOM_FIELDS.ARCHIVE_DIALOG.AUTOMATION_LINK' | translate }}
          <i class="material-icons">open_in_new</i>
        </a>
      </p>
    </div>
    <mat-dialog-actions>
      <button mat-stroked-button mat-dialog-close data-testid="archive-dialog-cancel">
        {{ 'COMMON.ACTION_LABELS.CANCEL' | translate }}
      </button>
      <button
        mat-flat-button
        color="primary"
        mat-dialog-close
        (click)="onArchiveField()"
        data-testid="archive-dialog-confirm"
      >
        {{ 'ADMINISTRATION.CUSTOM_FIELDS.ARCHIVE_DIALOG.ARCHIVE' | translate }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .material-icons {
        font-size: 14px;
        vertical-align: middle;
        padding-left: 2px;
      }

      .dialog-content {
        margin-bottom: 16px;
      }
    `,
  ],
  standalone: false,
})
export class ArchiveAlertDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ArchiveAlertDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: DialogData,
  ) {}

  onArchiveField(): void {
    this.dialogRef.close(true);
  }
}

@Component({
  template: `
    <div class="align">
      <ng-container *ngIf="element?.archived; else available">
        <button
          mat-icon-button
          (click)="validateAndUnarchive(element.id, element.name, element.externalId)"
          [attr.data-testid]="'unarchive-button-' + element.id"
        >
          <mat-icon>unarchive</mat-icon>
        </button>
      </ng-container>
      <ng-template #available>
        <span>
          <button
            mat-icon-button
            [matMenuTriggerFor]="menu"
            [attr.data-testid]="'archive-button-' + element.id"
            (click)="moreOptionsClicked($event)"
          >
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #menu="matMenu">
            <button mat-menu-item (click)="archive(element.id, element.name)">Archive</button>
          </mat-menu>
        </span>
      </ng-template>

      <button mat-icon-button (click)="edit(element.id, $event)" [attr.data-testid]="'edit-button-' + element.id">
        <mat-icon>edit</mat-icon>
      </button>
    </div>
  `,
  styles: [
    `
      .align {
        display: flex;
        flex-direction: row-reverse;
      }
    `,
  ],
  standalone: false,
})
export class ButtonsColumnComponent extends BaseColumnComponent {
  constructor(
    @Inject(SELECTED_OBJECT_TYPE$$) private readonly selectedObjectType$$: BehaviorSubject<ObjectType>,
    @Inject(ARCHIVE_FIELD_SERVICE) private readonly archiveFieldService: ArchiveFieldService,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private readonly fieldSelectedForEditService: FieldSelectedForEditService,
    private readonly modal: MatDialog,
    private readonly snackbar: SnackbarService,
    private readonly tableService: AuxiliaryDataTableService,
    private readonly trackingService: ProductAnalyticsService,
    private readonly translate: TranslateService,
    private readonly schemaService: SchemaService,
  ) {
    super();
  }

  edit(fieldId: string, $event: Event): void {
    // to not trigger a click on the table row
    $event.stopPropagation();

    // this one is a little bit trick to get immediately
    // the edit button when is clicked receives a focus
    // and when the side panel is closed most browsers try
    // to scroll to the element with focus
    // if the user clicks in a edit button, scroll the table
    // and closes the side panel, it will happen an unintended
    // scroll
    if (document.activeElement) {
      (document.activeElement as HTMLElement).blur();
    }
    this.fieldSelectedForEditService.editClickedForField(fieldId);
  }

  archive(fieldId: string, fieldName: string): void {
    combineLatest([
      this.partnerId$,
      this.modal
        .open(ArchiveAlertDialogComponent, {
          width: '440px',
          data: {
            fieldName: fieldName,
          },
        })
        .afterClosed(),
    ])
      .pipe(
        take(1),
        switchMap(([partnerId, archiveField]) => {
          if (!archiveField) {
            return;
          }
          return this.archiveFieldService.archiveFieldSchema(this.selectedObjectType$$.value, {
            fieldId: fieldId,
            partnerId: partnerId,
          });
        }),
        tap(() => this.tableService.reload()),
      )
      .subscribe(
        () => {
          this.snackbar.successSnack(
            this.translate.instant('ADMINISTRATION.CUSTOM_FIELDS.ARCHIVE_SUCCESS', {
              fieldName: fieldName,
            }),
          );
          this.trackingService.trackEvent(
            'CustomFields:archive-custom-field',
            'CustomFields',
            'archive-custom-field',
            0,
            {
              objectType: this.selectedObjectType$$.value,
            },
          );
        },
        () => {
          this.snackbar.errorSnack(
            this.translate.instant('ADMINISTRATION.CUSTOM_FIELDS.ARCHIVE_FAILURE', {
              fieldName: fieldName,
            }),
          );
        },
      );
  }

  validateAndUnarchive(fieldId: string, fieldName: string, externalId: string): void {
    if (!externalId) {
      this.unarchive(fieldId, fieldName);
      return;
    }
    this.partnerId$
      .pipe(
        take(1),
        switchMap((partnerId) =>
          this.schemaService.getMultiFieldSchema(this.selectedObjectType$$.value, [
            { partnerId: partnerId, externalId: externalId },
          ]),
        ),
      )
      .subscribe((resp) => {
        if (resp.fieldSchemas?.some((fieldSchema) => fieldSchema.fieldId)) {
          this.snackbar.errorSnack(
            this.translate.instant('COMMON.AUXILIARY_DATA.IDENTIFIER_DUPLICATE_MESSAGE'),
            this.translate.instant('COMMON.AUXILIARY_DATA.EDIT_IDENTIFIER'),
            null,
            () => this.fieldSelectedForEditService.editClickedForField(fieldId),
          );
        } else {
          this.unarchive(fieldId, fieldName);
        }
      });
  }

  private unarchive(fieldId: string, fieldName: string): void {
    this.partnerId$
      .pipe(
        take(1),
        switchMap((partnerId) =>
          this.archiveFieldService
            .unarchiveFieldSchema(this.selectedObjectType$$.value, { fieldId: fieldId, partnerId: partnerId })
            .pipe(
              take(1),
              tap(() => this.tableService.reload()),
            ),
        ),
      )
      .subscribe(
        () => {
          this.snackbar.successSnack(
            this.translate.instant('ADMINISTRATION.CUSTOM_FIELDS.UNARCHIVE_SUCCESS', {
              fieldName: fieldName,
            }),
          );
          this.trackingService.trackEvent(
            'CustomFields:unarchive-custom-field',
            'CustomFields',
            'unarchive-custom-field',
            0,
            {
              objectType: this.selectedObjectType$$.value,
            },
          );
        },
        () => {
          this.snackbar.errorSnack(
            this.translate.instant('ADMINISTRATION.CUSTOM_FIELDS.UNARCHIVE_FAILURE', {
              fieldName: fieldName,
            }),
          );
        },
      );
  }

  moreOptionsClicked($event: any): void {
    // to not trigger the click in the table row
    $event.stopPropagation();
  }
}

interface ArchiveFieldService {
  archiveFieldSchema(objectType: ObjectType, id: FieldSchemaIdentifier): Observable<HttpResponse<null>>;

  unarchiveFieldSchema(objectType: ObjectType, id: FieldSchemaIdentifier): Observable<HttpResponse<null>>;
}

const ARCHIVE_FIELD_SERVICE = new InjectionToken<ArchiveFieldService>('ArchiveFieldService');

@NgModule({
  exports: [ButtonsColumnComponent],
  declarations: [ButtonsColumnComponent, ArchiveAlertDialogComponent],
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    TranslateModule,
    MatMenuModule,
    GalaxyPipesModule,
  ],
  providers: [{ provide: ARCHIVE_FIELD_SERVICE, useClass: SchemaService }],
})
export class ButtonsColumnComponentModule {}
